import { Box, Button, Flex, Heading, Icon, Stack } from '@chakra-ui/react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useState } from 'react';
import { HotelFragmentFragment } from '../../../generated/types';
import { trackEvent } from '../../../utils/analytics';
import getIconForCategoryKey from '../../../utils/getIconForCategoryKey';
import { useStore } from '../../../store/provider';
import { jsonLdScriptProps } from 'react-schemaorg';
import { Hotel as HotelType, WithContext } from 'schema-dts';
import { useRouter } from 'next/router';
import Wrapper from '../Wrapper';
import ContactPopoverButton from './ContactPopoverButton';
import getHotelJsonLd from '../../../utils/getHotelJsonLd';
import PageHero from '../../Layout/PageHero';
import RatingForm from './Forms/RatingForm';
import { EditIcon } from '@chakra-ui/icons';
import BookingForm from './Forms/BookingForm';
import getAppComponent from '../../../utils/getAppComponent';
import { FaExternalLinkAlt } from '@react-icons/all-files/fa/FaExternalLinkAlt';
import { CustomStarFilled } from '../../../config/icons';
import { Link as ScrollLink } from 'react-scroll';

interface HotelProps {
  data: HotelFragmentFragment;
}

export const CTAStack = ({
  data,
  setShowRatingModal,
  setShowBookingModal,
  ...props
}) => (
  <Stack
    spacing={4}
    mt={4}
    direction={{
      base: 'column',
      md: 'row',
    }}
    {...props}
  >
    {!data.onlyShowOnlineBooking && (
      <ContactPopoverButton
        popoverColor="textColorBase"
        buttonProps={{
          width: 'auto',
        }}
        {...data}
      />
    )}

    {!data.onlyShowOnlineBooking && (
      <Button onClick={() => setShowBookingModal(true)}>Buchungsanfrage</Button>
    )}

    {data.onlyShowOnlineBooking && (
      <Button as="a" href={data.onlineBookingUrl} target="_blank">
        Online buchen <Icon ml={2} as={FaExternalLinkAlt} />
      </Button>
    )}

    <Button onClick={() => setShowRatingModal(true)}>
      <EditIcon marginRight={2} />
      Bewertung schreiben
    </Button>
  </Stack>
);

const Hotel: React.FC<HotelProps> = ({ data }) => {
  const store = useStore();
  const router = useRouter();

  let SHOULD_RENDER_FREIZEITHOTELS_TEASER = false;

  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const totalRatingCount = data.averageRating?.ratingCount || 0;

  const onResetRating = () => {
    // TODO: implement
  };

  useEffect(() => {
    if (showRatingModal) {
      trackEvent('HotelDetail', 'Open', 'Rating Modal');
    }
  }, [showRatingModal]);

  if (data.frontends) {
    data.frontends.forEach((frontend) => {
      if (frontend.name === 'Freizeithotels') {
        // this hotel is also visible for freizeithotels
        if (store.config.renderConfig.freizeithotelsTeaser) {
          SHOULD_RENDER_FREIZEITHOTELS_TEASER = true;
        }
      }
    });
  }

  const jsonLD = useMemo<WithContext<HotelType>>(() => getHotelJsonLd(data), [
    data,
  ]);

  const Icon = getIconForCategoryKey('hotel');

  const HotelContent = getAppComponent(
    process.env.APP_ID!,
    store.config.usesNewLayout,
    'HotelContent'
  );

  return (
    <Wrapper typename={data.__typename}>
      <Box
        {...{
          'data-track-content': '',
          'data-content-name': 'Hotel Detail',
          'data-content-target': data.title,
        }}
      >
        <Head>
          <script {...jsonLdScriptProps<HotelType>(jsonLD)} />
        </Head>

        <RatingForm
          isOpen={showRatingModal}
          hotelId={data.id}
          onClose={() => setShowRatingModal(false)}
          onSubmitted={onResetRating}
        />

        {!data.onlyShowOnlineBooking && (
          <BookingForm
            isOpen={showBookingModal}
            hotelId={data.id}
            onClose={() => setShowBookingModal(false)}
          />
        )}

        <PageHero
          darken
          imageUrl={data.teaserImage?.hero.url}
          imageTitle={data.teaserImage?.imageTitle}
          imageAlt={data.teaserImage?.imageAlt}
          imageCopyright={data.teaserImage?.copyright}
          copyrightPosition="top"
        >
          <Box>
            <Flex align="center">
              <Box
                padding={2}
                borderRadius="lg"
                display="inline-flex"
                fontSize={{
                  base: 'md',
                  md: '2xl',
                }}
                border="2px solid"
                borderColor="#fff"
                color="textColor"
              >
                <Icon />
              </Box>
              <Box
                color="textColor"
                fontSize={{
                  base: 'md',
                  md: 'xl',
                }}
                marginLeft={4}
              >
                {data.country?.name} | {data.region?.name}
              </Box>
            </Flex>
            <Heading
              my={4}
              as="h1"
              fontSize={{
                base: '2xl',
                md: '4xl',
              }}
            >
              {data.title}
            </Heading>
            <Box
              fontSize={{
                base: 'lg',
                md: 'xl',
              }}
            >
              <Box>
                {data.postcode} {data.city}
              </Box>
            </Box>
            <CTAStack
              setShowRatingModal={setShowRatingModal}
              setShowBookingModal={setShowBookingModal}
              data={data}
              display={{
                base: 'none',
                md: 'flex',
              }}
            />
            {data.ratings?.total && (
              <ScrollLink to="bewertungen" smooth>
                <Flex mt={2} alignItems="center" cursor="pointer">
                  <CustomStarFilled marginRight={1} fill="starColor" />{' '}
                  {data.averageRating.total.toFixed(1)} ({data.ratings?.total}{' '}
                  Bewertungen)
                </Flex>
              </ScrollLink>
            )}
          </Box>
        </PageHero>

        {HotelContent && (
          <HotelContent
            data={data}
            setShowRatingModal={setShowRatingModal}
            setShowBookingModal={setShowBookingModal}
          />
        )}
      </Box>
    </Wrapper>
  );
};

export default Hotel;
